'use client'

import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { ScoringMethodologyNote } from '@/components/radar/ScoringMethodologyNote'
import type {
  PersistedBrandDisambiguationResult,
  BrandDisambiguationProviderDetail,
} from '@/lib/tools/brand-disambiguation'
import type {
  EntityConfusionFlag,
  ProviderIdentityVerdict,
} from '@/lib/radar/brand-disambiguation'

/**
 * Full (unlocked) renderer for a Brand Disambiguation run. Mirrors the teaser
 * ExampleReport layout on the public page, but with real data and the details
 * that were locked behind the wall: the exact same-named entity each engine
 * confused the brand with, the evidence, the verified identity, and the fixes.
 *
 * Theming follows the data-theme='dark' rules (no bg-white / gray, no dark:):
 * bg-muted / bg-background / border-border / text-foreground.
 */

const SEVERITY: Record<
  'high' | 'medium' | 'low',
  { label: string; color: string }
> = {
  high: { label: 'High', color: '#F90B8A' },
  medium: { label: 'Medium', color: '#f97316' },
  low: { label: 'Low', color: '#FDC304' },
}

const PRIORITY_COLOR: Record<'high' | 'medium' | 'low', string> = {
  high: '#F90B8A',
  medium: '#f97316',
  low: '#22c55e',
}

const SEVERITY_RANK = { low: 1, medium: 2, high: 3 } as const

type PersistedProviderDetail = Omit<
  BrandDisambiguationProviderDetail,
  'verdict'
> & {
  verdict?: ProviderIdentityVerdict
}

interface DisplayProviderDetail {
  provider: string
  verdict: ProviderIdentityVerdict
  flag: EntityConfusionFlag | null
  legacyObservationCount: number
  currentMethod: boolean
}

function normalizeProviderDetail(
  detail: PersistedProviderDetail
): DisplayProviderDetail {
  // Persisted reports created before prompt/scoring v1.1.0 have flags but no
  // provider verdict. Normalize those only for rendering; never rewrite their
  // stored score or evidence.
  const flags = Array.isArray(detail.flags) ? detail.flags : []
  const flag = flags.reduce<EntityConfusionFlag | null>(
    (worst, candidate) =>
      !worst ||
      SEVERITY_RANK[candidate.severity] > SEVERITY_RANK[worst.severity]
        ? candidate
        : worst,
    null
  )

  if (detail.verdict) {
    return {
      provider: detail.provider,
      verdict: detail.verdict,
      flag,
      legacyObservationCount: 0,
      currentMethod: true,
    }
  }

  if (flag) {
    return {
      provider: detail.provider,
      verdict: {
        status: 'confused',
        severity: flag.severity,
        reason: 'Legacy collision evidence',
      },
      flag,
      legacyObservationCount: flags.length,
      currentMethod: false,
    }
  }

  return {
    provider: detail.provider,
    verdict: {
      status: 'indeterminate',
      severity: null,
      reason: 'No collision recorded (legacy method)',
    },
    flag: null,
    legacyObservationCount: 0,
    currentMethod: false,
  }
}

function gradeColor(score: number): string {
  if (score >= 85) return '#22c55e'
  if (score >= 70) return '#008dcc'
  if (score >= 50) return '#eab308'
  if (score >= 30) return '#f97316'
  return '#F90B8A'
}

export default function BrandDisambiguationResults({
  result,
}: {
  result: PersistedBrandDisambiguationResult
}) {
  const withheld =
    result.scoreStatus === 'withheld' ||
    result.overallScore == null ||
    result.grade == null
  // The withheld state means "we could not verify this", which is the semantic
  // warning token — not a grade on the score ramp. Uses the design-system
  // variable rather than a hex literal, so it follows the theme. The grade
  // ramp below still uses literals because it is a continuous scale with no
  // token equivalent; this state is categorical.
  const color = withheld
    ? 'var(--warning-text)'
    : gradeColor(result.overallScore ?? 0)
  // `${color}15` hex-alpha does not work on a CSS variable, so the tint is
  // composed with color-mix (the pattern already used in NewAuditView).
  const tint = withheld
    ? 'color-mix(in srgb, var(--warning-text) 10%, transparent)'
    : `${color}15`
  const gt = result.groundTruth
  const providerRows = result.providerDetails.map(normalizeProviderDetail)
  const currentMethod = providerRows.every(row => row.currentMethod)
  const judgedCount = Array.isArray(result.judgedProviders)
    ? result.judgedProviders.length
    : providerRows.filter(row => row.verdict.status !== 'indeterminate').length
  const legacyConfusedEngines = providerRows.filter(
    row => row.verdict.status === 'confused'
  ).length
  const legacyObservations = providerRows.reduce(
    (sum, row) => sum + row.legacyObservationCount,
    0
  )
  const scoreSummary = withheld
    ? 'No provider identity verdict could be established'
    : currentMethod
      ? result.totalConfusions === 0
        ? 'No judged engines described the wrong entity'
        : `${result.totalConfusions} of ${judgedCount} judged ${judgedCount === 1 ? 'engine' : 'engines'} showed an identity collision`
      : legacyConfusedEngines === 0
        ? 'No collision observations recorded (legacy method)'
        : `${legacyConfusedEngines} ${legacyConfusedEngines === 1 ? 'engine has' : 'engines have'} ${legacyObservations} legacy collision ${legacyObservations === 1 ? 'observation' : 'observations'}`
  const displayedRecommendations =
    currentMethod || (legacyConfusedEngines > 0 && !withheld)
      ? result.recommendations
      : []
  const displayedSummary =
    currentMethod || withheld
      ? result.aiSummary?.summary
      : legacyConfusedEngines > 0
        ? `This legacy report recorded ${legacyObservations} collision ${legacyObservations === 1 ? 'observation' : 'observations'} across ${legacyConfusedEngines} ${legacyConfusedEngines === 1 ? 'engine' : 'engines'}. Its stored score and grade use the legacy observation-based method.`
        : 'This legacy report recorded no collision observations. It did not retain provider-level verdicts, so correct identification cannot be confirmed from the stored result.'
  const displayedActions =
    currentMethod || withheld ? result.aiSummary?.actions || [] : []

  return (
    <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
      {/* Header */}
      <div className='flex items-center justify-between mb-5 gap-3 flex-wrap'>
        <span className='text-[11px] font-bold uppercase tracking-widest text-muted-foreground leading-none'>
          Brand Disambiguation report
        </span>
        <span className='text-[11px] font-medium text-muted-foreground leading-none'>
          {result.brand} &middot; {result.domain}
        </span>
      </div>

      <ScoringMethodologyNote
        toolSlug='brand-disambiguation'
        className='mb-6'
      />

      {/* Score + grade. A withheld run shows no number and no letter: nothing
          was verified, and a placeholder digit would read as a measurement. */}
      <div className='flex flex-col items-center gap-3 mb-6'>
        <p className='text-5xl font-black leading-none' style={{ color }}>
          {withheld ? '--' : result.overallScore}
        </p>
        <span
          className='inline-block text-sm font-bold px-3 py-1'
          style={{ backgroundColor: tint, color }}
        >
          {withheld ? 'Not verified' : `Grade ${result.grade}`}
        </span>
        <p className='text-xs text-muted-foreground leading-none'>
          {scoreSummary}
        </p>
      </div>

      {/* Severity breakdown */}
      <div className='grid grid-cols-3 gap-3 mb-6'>
        {(['high', 'medium', 'low'] as const).map(sev => (
          <div
            key={sev}
            className='bg-background border border-border/60 p-4 text-center'
          >
            <p
              className='text-2xl font-black leading-none mb-1'
              style={{ color: SEVERITY[sev].color }}
            >
              {result.bySeverity[sev]}
            </p>
            <p className='text-[11px] uppercase tracking-wide text-muted-foreground leading-none'>
              {SEVERITY[sev].label}
              {!currentMethod ? ' observations' : ''}
            </p>
          </div>
        ))}
      </div>

      {/* Per-engine findings */}
      <div className='space-y-2 mb-6'>
        {providerRows.map(row => {
          if (row.verdict.status === 'correct') {
            return (
              <div
                key={row.provider}
                className='flex items-center gap-3 bg-background border border-border/60 p-3'
              >
                <CheckCircle2 size={14} className='text-[#22c55e] shrink-0' />
                <span className='text-sm font-medium text-foreground leading-none w-24 shrink-0'>
                  {row.provider}
                </span>
                <span className='text-xs text-muted-foreground leading-none'>
                  Identified correctly
                </span>
              </div>
            )
          }

          if (row.verdict.status === 'indeterminate' || !row.flag) {
            return (
              <div
                key={row.provider}
                className='flex items-center gap-3 bg-background border border-border/60 p-3'
              >
                <AlertTriangle
                  size={14}
                  className='text-[var(--warning-text)] shrink-0'
                />
                <span className='text-sm font-medium text-foreground leading-none w-24 shrink-0'>
                  {row.provider}
                </span>
                <span className='text-xs text-muted-foreground leading-relaxed'>
                  {row.currentMethod
                    ? 'Could not verify this engine'
                    : row.verdict.reason}
                </span>
              </div>
            )
          }

          const flag = row.flag
          return (
            <div
              key={row.provider}
              className='bg-background border border-border/60 p-3'
            >
              <div className='flex items-center gap-3 mb-2'>
                <AlertTriangle size={14} className='text-[#F90B8A] shrink-0' />
                <span className='text-sm font-medium text-foreground leading-none w-24 shrink-0'>
                  {row.provider}
                </span>
                <span className='text-xs text-muted-foreground leading-none'>
                  Confused with
                </span>
                <span className='text-xs font-semibold text-foreground leading-none'>
                  {flag.confusedWith}
                </span>
                <span
                  className='ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 shrink-0'
                  style={{
                    backgroundColor: `${SEVERITY[flag.severity].color}15`,
                    color: SEVERITY[flag.severity].color,
                  }}
                >
                  {SEVERITY[flag.severity].label}
                </span>
              </div>
              {flag.evidence && (
                <p className='text-xs text-foreground/70 leading-relaxed pl-7'>
                  &ldquo;{flag.evidence}&rdquo;
                </p>
              )}
            </div>
          )
        })}
      </div>

      {/* Verified identity */}
      {(gt.companyName || gt.description || gt.products.length > 0) && (
        <div className='bg-background border border-border/60 p-4 mb-6'>
          <h3 className='text-sm font-semibold text-foreground mb-2'>
            Your verified identity (ground truth)
          </h3>
          {gt.companyName && (
            <p className='text-xs text-foreground/80 mb-1'>
              <span className='text-muted-foreground'>Name: </span>
              {gt.companyName}
            </p>
          )}
          {gt.description && (
            <p className='text-xs text-foreground/80 mb-1'>
              <span className='text-muted-foreground'>Description: </span>
              {gt.description}
            </p>
          )}
          {gt.products.length > 0 && (
            <p className='text-xs text-foreground/80 mb-1'>
              <span className='text-muted-foreground'>Products: </span>
              {gt.products.join(', ')}
            </p>
          )}
          {gt.headquarters && (
            <p className='text-xs text-foreground/80'>
              <span className='text-muted-foreground'>HQ: </span>
              {gt.headquarters}
            </p>
          )}
        </div>
      )}

      {/* Recommendations */}
      {displayedRecommendations.length > 0 && (
        <div className='mb-6'>
          <h3 className='text-sm font-semibold text-foreground mb-3'>
            Fixes to reclaim your identity
          </h3>
          <div className='space-y-2'>
            {displayedRecommendations.map((rec, i) => (
              <div
                key={i}
                className='bg-background border border-border/60 p-3 flex items-start gap-3'
              >
                <span
                  className='text-[10px] font-bold uppercase px-1.5 py-0.5 shrink-0 mt-0.5'
                  style={{
                    backgroundColor: `${PRIORITY_COLOR[rec.priority]}15`,
                    color: PRIORITY_COLOR[rec.priority],
                  }}
                >
                  {rec.priority}
                </span>
                <p className='text-xs text-foreground/80 leading-relaxed'>
                  {rec.message}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI summary */}
      {displayedSummary && (
        <div className='bg-background border border-border/60 p-4'>
          <h3 className='text-sm font-semibold text-foreground mb-2'>
            Summary
          </h3>
          <p className='text-xs text-foreground/80 leading-relaxed mb-3'>
            {displayedSummary}
          </p>
          {displayedActions.length > 0 && (
            <ul className='space-y-1'>
              {displayedActions.map((action, i) => (
                <li
                  key={i}
                  className='text-xs text-foreground/70 leading-relaxed flex gap-2'
                >
                  <span className='text-muted-foreground shrink-0'>&bull;</span>
                  {action}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
